import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Country } from '~shared/models';
import { CHECKOUT_LOCALSTORAGE_REACT_PHONENUMBER, CHECKOUT_STATE_ID, PaymentMethodOptions } from '../constants/checkout.constants';

import { Address } from '../models';
import * as fromAddresses from './addresses';
import * as fromCountries from './countries';
import * as fromOrder from './order';
import * as fromPayment from './payment';
import * as fromPaymentMethods from './payment-methods';
import { CheckoutCommonUtil } from '../utils/checkout-common-util';

export interface State {
  addresses: fromAddresses.State;
  countries: fromCountries.State;
  order: fromOrder.State;
  paymentMethods: fromPaymentMethods.State;
  payment: fromPayment.PaymentState;
}

const checkoutModuleState = createFeatureSelector<State>(CHECKOUT_STATE_ID);

// Slice addresses
const getAddressesState = createSelector(checkoutModuleState, (state: State) => state.addresses);

const getAddressesLoading = createSelector(getAddressesState, (state: fromAddresses.State) => state.loading);

const getAllAddresses = createSelector(getAddressesState, fromAddresses.selectAllAddresses);

const getAddresses = createSelector(getAllAddresses, (addresses: Address[]) => addresses.filter((address) => address));

const getCreateLoading = createSelector(checkoutModuleState, (state: State) => state.addresses.loadingCreate);

const getCreateSuccess = createSelector(checkoutModuleState, (state: State) => state.addresses.successCreate);

const getCreatedShippingSuccess = createSelector(checkoutModuleState, (state: State) => state.addresses.isShipping);

const getCreateError = createSelector(getAddressesState, (state: fromAddresses.State) => state.errorCreate);

const getUpdateLoading = createSelector(checkoutModuleState, (state: State) => state.addresses.loadingUpdate);

const getUpdateSuccess = createSelector(checkoutModuleState, (state: State) => state.addresses.successUpdate);

const getSelectedBillingAddressId = createSelector(
  getAddressesState,
  (state: fromAddresses.State) => state.selectedBilling
);

const getSelectedShippingAddressId = createSelector(
  getAddressesState,
  (state: fromAddresses.State) => state.selectedShipping
);

const getSortedBillingAddresses = createSelector(
  getAddresses,
  getSelectedBillingAddressId,
  (addresses: Address[], selectedId) => moveSelectedAddressOnTop(addresses, selectedId)
);

const getSortedShippingAddresses = createSelector(
  getAddresses,
  getSelectedShippingAddressId,
  (addresses: Address[], selectedId) => moveSelectedAddressOnTop(addresses, selectedId)
);

const getPreservedAddressesLoading = createSelector(
  getAddressesState,
  (state: fromAddresses.State) => state.loadingPreserved
);

const getPreservedAddressesFailure = createSelector(
  getAddressesState,
  (state: fromAddresses.State) => state.errorPreserved
);

const getPreservedAddresses = createSelector(getAddressesState, (state: fromAddresses.State) => state.preserved);

const getBillingAsShipping = createSelector(getAddressesState, (state: fromAddresses.State) => state.billingAsShipping);

const getFreightForwarding = createSelector(getAddressesState, (state: fromAddresses.State) => {
  return CheckoutCommonUtil.getPhoneNumberFromReactLocal() ?? state.freightForwarding;
});

const getSelectedOrDefaultBillingAddress = createSelector(
  getAddresses,
  getSelectedBillingAddressId,
  (addresses: Address[], selectedId): Address => {
    if (selectedId) {
      return addresses.find((address: Address) => address.id === selectedId);
    }
    return addresses.find((address: Address) => address.defaultBilling);
  }
);

const getSelectedOrDefaultShippingAddress = createSelector(
  getAddresses,
  getSelectedShippingAddressId,
  (addresses: Address[], selectedId): Address => {
    if (selectedId) {
      return addresses.find((address: Address) => address.id === selectedId);
    }
    return addresses.find((address: Address) => address.defaultShipping);
  }
);

const getSelectedBillingAddress = createSelector(
  getAddresses,
  getSelectedBillingAddressId,
  (addresses: Address[], selectedBillingId): Address => {
    if (selectedBillingId) {
      return addresses.find((address: Address) => address.id === selectedBillingId);
    }
    return null;
  }
);

const getSelectedShippingAddress = createSelector(
  getAddresses,
  getSelectedShippingAddressId,
  (addresses: Address[], selectedShippingId): Address => {
    if (selectedShippingId) {
      return addresses.find((address: Address) => address.id === selectedShippingId);
    }
    return null;
  }
);

const getIsShippingAddressValidationInProcess = createSelector(
  checkoutModuleState,
  (state: State) => state.addresses.isShippingAddressValidationInProcess
);

const getIsShippingAddressValidationSuccessful = createSelector(
  checkoutModuleState,
  (state: State) => state.addresses.isShippingAddressValidationSuccessful
);

export const addressesSelectors = {
  getLoading: getAddressesLoading,
  getAddresses,
  getCreateLoading,
  getCreateSuccess,
  getCreatedShippingSuccess,
  getCreateError,
  getUpdateLoading,
  getUpdateSuccess,
  getSelectedBillingAddress,
  getSelectedShippingAddress,
  getSelectedOrDefaultBillingAddress,
  getSelectedOrDefaultShippingAddress,
  getSortedBillingAddresses,
  getSortedShippingAddresses,
  getSelectedBillingAddressId,
  getSelectedShippingAddressId,
  getPreservedAddressesLoading,
  getPreservedAddresses,
  getFreightForwarding,
  getPreservedAddressesFailure,
  getBillingAsShipping,
  getIsShippingAddressValidationInProcess,
  getIsShippingAddressValidationSuccessful
};

// Slice countries
const getCountriesState = createSelector(checkoutModuleState, (state: State) => state.countries);

const getCountriesLoading = createSelector(getCountriesState, (state: fromCountries.State) => state.loading);

const getCountries = createSelector(getCountriesState, (state: fromCountries.State) => state.countries);

const getDefaultCountry = createSelector(getCountries, (countries: Country[]) =>
  countries.find((country: Country) => country.default)
);

export const countriesSelectors = {
  getLoading: getCountriesLoading,
  getCountries,
  getDefaultCountry,
};

// Slice order

const getOrderState = createSelector(checkoutModuleState, (state: State) => state.order);

const getOrderLoading = createSelector(getOrderState, (state: fromOrder.State) => state.loading);

const getOrder = createSelector(getOrderState, (state: fromOrder.State) => state && state.payload);

const getVatIdValidity = createSelector(
  getOrderState, (state: fromOrder.State) => state && state.payload?.isVatIdValid
);

export const orderSelectors = {
  getOrderLoading,
  getOrder,
  getVatIdValidity,
};

function moveSelectedAddressOnTop(addresses: Address[], selectedId: string) {
  const selectedAddress = addresses.find((item) => item.id === selectedId);
  const selectedAddressIndex = addresses.indexOf(selectedAddress);
  if (!!selectedAddress) {
    return selectedAddressIndex === 0
      ? addresses
      : [selectedAddress, ...addresses.filter((item) => item.id !== selectedId)];
  }
  return [];
}

const getPaymentState = createSelector(checkoutModuleState, (state: State) => state.payment);

const getPaymentLoading = createSelector(checkoutModuleState, (state: State) => state.payment.loading);

const getPaymentRequest = createSelector(checkoutModuleState, (state: State) => state.payment.request);

const getPaymentPayload = createSelector(getPaymentState, (state: fromPayment.PaymentState) => state && state.payload);

const getPaymentWidgetConfig = createSelector(getPaymentPayload, (payload) => payload && payload.paymentWidget);

const getLastPaymentMethodUsed = createSelector(
  getPaymentPayload,
  (payload) => payload && payload.lastPaymentMethodUsed
);

const getPaymentWidgetCreditCardConfig = createSelector(
  getPaymentWidgetConfig,
  (payload) => payload && payload[PaymentMethodOptions.CreditCard]
);

const getPaidWithPaymentMethod = createSelector(
  getPaymentPayload,
  (payload) => payload && payload.payment && payload.payment.response && payload.payment.response.paymentMethod
);

const getPaymentFailure = createSelector(getPaymentState, (state) => state && state.error);

const getPaymentFailureOrderNumber = createSelector(getPaymentState, (state) => state && state.error?.orderNumber);

export const paymentSelectors = {
  getPaymentFailure,
  getPaymentFailureOrderNumber,
  getLastPaymentMethodUsed,
  getPaymentWidgetConfig,
  getPaymentLoading,
  getPaidWithPaymentMethod,
  getPaymentWidgetCreditCardConfig,
  getPaymentRequest,
};
